import type { CSSProperties, Touch } from 'react';
import React, { Component } from 'react';

import Items from './items/Items';
import Sidebar from './layout/Sidebar';
import Columns from './columns/Columns';
import GroupRows from './row/GroupRows';
import ScrollElement from './scroll/ScrollElement';
import MarkerCanvas from './markers/MarkerCanvas';
import windowResizeDetector from '../resize-detector/window';

import type { ItemDimensions } from './utility/calendar';
import {
  calculateScrollCanvas,
  calculateTimeForXPosition,
  coordinateToTimeRatio,
  getCanvasBoundariesFromVisibleTime,
  getCanvasWidth,
  getMinUnit,
  stackTimelineItems,
  timeToCoordinateRatio,
} from './utility/calendar';
import { _get, _length } from './utility/generic';
import {
  defaultHeaderLabelFormats,
  defaultKeys,
  defaultSubHeaderLabelFormats,
  defaultTimeSteps,
} from './default-config';
import { TimelineStateProvider } from './timeline/TimelineStateContext';
import { TimelineMarkersProvider } from './markers/TimelineMarkersContext';
import { TimelineHeadersProvider } from './headers/HeadersContext';
import TimelineHeaders from './headers/TimelineHeaders';
import DateHeader from './headers/DateHeader';
import type { Moment } from 'moment';
import type {
  TimelineContextValue,
  TimelineGroupBase,
  TimelineItemBase,
} from './types';
import type {
  GetItemPropsParams,
  ItemContext,
  ItemRendererGetResizePropsReturnType,
  RealItemProps,
} from './items/Item';

export type TimeSteps = {
  second: number;
  minute: number;
  hour: number;
  day: number;
  week: number;
  isoWeek: number;
  month: number;
  year: number;
};

export type Unit = keyof TimeSteps;

export type ReactCalendarGroupRendererProps<
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
> = {
  group: CustomGroup;
  isRightSidebar: boolean | undefined;
};

export type Keys<
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
  CustomItem extends TimelineItemBase = TimelineItemBase,
> = {
  groupIdKey: keyof CustomGroup;
  groupTitleKey: keyof CustomGroup;
  groupLabelKey: keyof CustomGroup;
  groupRightTitleKey: keyof CustomGroup;
  itemIdKey: keyof CustomItem;
  itemTitleKey: keyof CustomItem;
  itemDivTitleKey: keyof CustomItem;
  itemGroupKey: keyof CustomItem;
  itemTimeStartKey: keyof CustomItem;
  itemTimeEndKey: keyof CustomItem;
};

export type HeaderLabelFormats = {
  yearShort: string;
  yearLong: string;
  monthShort: string;
  monthMedium: string;
  monthMediumLong: string;
  monthLong: string;
  dayShort: string;
  dayLong: string;
  hourShort: string;
  hourMedium: string;
  hourMediumLong: string;
  hourLong: string;
};

export type SubHeaderLabelFormats = {
  yearShort: string;
  yearLong: string;
  monthShort: string;
  monthMedium: string;
  monthLong: string;
  dayShort: string;
  dayMedium: string;
  dayMediumLong: string;
  dayLong: string;
  hourShort: string;
  hourLong: string;
  minuteShort: string;
  minuteLong: string;
};

export type ReactCalendarTimelineProps<
  CustomItem extends TimelineItemBase = TimelineItemBase,
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
> = {
  groups: CustomGroup[];
  items: CustomItem[];
  sidebarWidth?: number;
  rightSidebarWidth?: number;
  dragSnap?: number;
  minResizeWidth?: number;
  stickyHeader?: boolean;
  lineHeight?: number;
  itemHeightRatio?: number;
  headerHeight?: number;

  minZoom?: number;
  maxZoom?: number;
  minScale?: number;
  maxScale?: number;

  clickTolerance?: number;

  canChangeGroup?: boolean;
  canMove?: boolean;
  canResize?: true | false | 'left' | 'right' | 'both';
  useResizeHandle?: boolean;
  canSelect?: boolean;

  stackItems?: boolean;

  itemTouchSendsClick?: boolean;

  horizontalLineClassNamesForGroup?: (group: CustomGroup) => string[];

  onItemMove?: (
    item: string | number,
    dragTime: number,
    newGroupOrder: number,
  ) => void;
  onItemResize?: (
    item: string | number,
    resizeTime: number,
    edge: null | 'left' | 'right',
  ) => void;
  onItemClick?: (
    item: string | number,
    e: React.MouseEvent | React.TouchEvent,
    time: number,
  ) => void;
  onItemSelect?: (
    item: string | number,
    e: React.MouseEvent | React.TouchEvent,
    time: number,
  ) => void;
  onItemDeselect?: (e?: React.MouseEvent | React.TouchEvent) => void;
  onCanvasClick?: (groupId: string, time: number, e: React.MouseEvent) => void;
  onItemDoubleClick?: (
    item: string | number,
    e: React.MouseEvent,
    time: number,
  ) => void;
  onItemContextMenu?: (
    item: string | number,
    e: React.MouseEvent,
    time: number,
  ) => void;
  onItemTap?: (
    itemId: string | number,
    e: React.TouchEvent<HTMLElement>,
  ) => void;
  onCanvasDoubleClick?: (
    groupId: string,
    time: number,
    e: React.MouseEvent,
  ) => void;
  onCanvasContextMenu?: (
    groupId: string,
    time: number,
    e: React.MouseEvent,
  ) => void;
  onZoom?: (value: TimelineContextValue) => void;
  onItemDrag?: (data: ItemDragData) => void;

  moveResizeValidator?: (
    event: 'move' | 'resize',
    item: CustomItem,
    dragTime: number | Moment,
    resizeEdge?: null | 'left' | 'right',
  ) => number;

  itemRenderer?: React.FunctionComponent<{
    item: CustomItem;
    timelineContext: TimelineContextValue;
    itemContext: ItemContext;
    getItemProps: (props: GetItemPropsParams) => RealItemProps;
    getResizeProps: () => ItemRendererGetResizePropsReturnType;
  }>;
  groupRenderer?: React.ComponentType<
    ReactCalendarGroupRendererProps<CustomGroup>
  >;

  style?: CSSProperties;

  keys?: Keys<CustomGroup, CustomItem>;
  headerRef?: (el: HTMLDivElement | null) => void;
  scrollRef?: (el: HTMLDivElement | null) => void;

  timeSteps?: TimeSteps;

  defaultTimeStart?: Moment | Date;
  defaultTimeEnd?: Moment | Date;

  visibleTimeStart?: number;
  visibleTimeEnd?: number;
  onTimeChange?: (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: (
      visibleTimeStart: number,
      visibleTimeEnd: number,
    ) => void,
  ) => void;
  onBoundsChange?: (from: number, to: number) => void;

  selected?: (string | number)[];

  headerLabelFormats?: HeaderLabelFormats;

  subHeaderLabelFormats?: SubHeaderLabelFormats;

  resizeDetector?: {
    addListener: (c: Component) => void;
    removeListener: (c: Component) => void;
  };

  verticalLineClassNamesForTime?: (
    start: number,
    end: number,
  ) => string[] | undefined;
  isReadOnlyScenario?: boolean;

  children?: React.ReactNode;
};

export type ReactCalendarTimelineState<
  CustomItem extends TimelineItemBase = TimelineItemBase,
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
> = {
  groups: CustomGroup[];
  items: CustomItem[];
  canvasTimeEnd: number;
  visibleTimeEnd: number;
  resizingItem: null | string | number;
  draggingItem: null | string | number;
  dragTime: null | number;
  selectedItem: null | string | number;
  sidebarWidth: number;
  rightSidebarWidth: number;
  width: number;
  height: number;
  resizingEdge: null | 'left' | 'right';
  visibleTimeStart: number;
  canvasTimeStart: number;
  resizeTime: null | number;
  dragGroupTitle: string | null;
  newGroupOrder: null | number;
  dimensionItems: ItemDimensions[];
  groupHeights: number[];
  groupTops: number[];
  timeRatio: number;
  pixelsPerMs: number;
};

export type ItemDragData = {
  eventType: string;
  itemId: string | number;
  time: number;
  newGroupOrder?: number;
  edge?: null | 'left' | 'right';
};

export const defaultLineHeight = 30;
export const defaultItemHeightRatio = 0.65;
export const defaultStackItems = false;

const defaultProps = {
  sidebarWidth: 150,
  rightSidebarWidth: 0,
  dragSnap: 1000 * 60 * 15, // 15min
  minResizeWidth: 20,
  stickyHeader: true,
  lineHeight: defaultLineHeight,
  itemHeightRatio: defaultItemHeightRatio,

  minZoom: 60 * 60 * 1000, // 1 hour
  maxZoom: 5 * 365.24 * 86400 * 1000, // 5 years
  minScale: 0.9,
  maxScale: 1.1,

  clickTolerance: 3, // how many pixels can we drag for it to be still considered a click?

  canChangeGroup: true,
  canMove: true,
  canResize: 'right',
  useResizeHandle: false,
  canSelect: true,

  stackItems: defaultStackItems,

  horizontalLineClassNamesForGroup: null,

  onItemMove: null,
  onItemResize: null,
  onItemClick: null,
  onItemSelect: null,
  onItemDeselect: null,
  onItemDrag: null,
  onCanvasClick: null,
  onItemDoubleClick: null,
  onItemContextMenu: null,
  onItemTap: null,
  onZoom: null,

  verticalLineClassNamesForTime: null,

  moveResizeValidator: null,

  dayBackground: null,

  defaultTimeStart: null,
  defaultTimeEnd: null,

  itemTouchSendsClick: false,

  style: {},
  keys: defaultKeys,
  timeSteps: defaultTimeSteps,
  headerRef: () => {},
  scrollRef: () => {},

  // if you pass in visibleTimeStart and visibleTimeEnd, you must also pass onTimeChange(visibleTimeStart, visibleTimeEnd),
  // which needs to update the props visibleTimeStart and visibleTimeEnd to the ones passed
  visibleTimeStart: null,
  visibleTimeEnd: null,
  onTimeChange: function (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: (
      visibleTimeStart: number,
      visibleTimeEnd: number,
    ) => void,
  ) {
    updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
  },
  // called when the canvas area of the calendar changes
  onBoundsChange: null,
  children: null,

  headerLabelFormats: defaultHeaderLabelFormats,
  subHeaderLabelFormats: defaultSubHeaderLabelFormats,

  selected: null,
};

export default class ReactCalendarTimeline<
  CustomItem extends TimelineItemBase = TimelineItemBase,
  CustomGroup extends TimelineGroupBase = TimelineGroupBase,
  Props extends ReactCalendarTimelineProps<
    CustomItem,
    CustomGroup
  > = ReactCalendarTimelineProps<CustomItem, CustomGroup>,
> extends Component<Props, ReactCalendarTimelineState> {
  static defaultProps = defaultProps;

  lastTouchDistance: null | number;
  scrollComponent: HTMLDivElement;
  container: HTMLDivElement | null;

  constructor(props: Props) {
    super(props);

    this.getSelected = this.getSelected.bind(this);
    this.hasSelectedItem = this.hasSelectedItem.bind(this);
    this.isItemSelected = this.isItemSelected.bind(this);

    let visibleTimeStart = null;
    let visibleTimeEnd = null;

    if (this.props.defaultTimeStart && this.props.defaultTimeEnd) {
      visibleTimeStart = this.props.defaultTimeStart.valueOf();
      visibleTimeEnd = this.props.defaultTimeEnd.valueOf();
    } else if (this.props.visibleTimeStart && this.props.visibleTimeEnd) {
      visibleTimeStart = this.props.visibleTimeStart;
      visibleTimeEnd = this.props.visibleTimeEnd;
    } else {
      // throwing an error because neither default nor visible time props provided
      throw new Error(
        'You must provide either "defaultTimeStart" and "defaultTimeEnd" or "visibleTimeStart" and "visibleTimeEnd" to initialize the Timeline',
      );
    }

    const [canvasTimeStart, canvasTimeEnd] = getCanvasBoundariesFromVisibleTime(
      visibleTimeStart,
      visibleTimeEnd,
    );

    const canvasWidth = getCanvasWidth(1000);
    const timeRatio = coordinateToTimeRatio(
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
    );
    const pixelsPerMs = timeToCoordinateRatio(
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
    );

    const state: ReactCalendarTimelineState = {
      groups: props.groups,
      items: props.items,
      canvasTimeEnd: canvasTimeEnd,
      visibleTimeEnd: visibleTimeEnd,
      resizingItem: null,
      draggingItem: null,
      dragTime: null,
      selectedItem: null,
      sidebarWidth: props.sidebarWidth ?? 200,
      rightSidebarWidth: props.rightSidebarWidth ?? 0,
      width: 1000,
      height: 1000,
      resizingEdge: null,
      visibleTimeStart: visibleTimeStart,
      canvasTimeStart: canvasTimeStart,
      resizeTime: null,
      dragGroupTitle: null,
      newGroupOrder: null,
      dimensionItems: [],
      groupHeights: [],
      groupTops: [],
      timeRatio,
      pixelsPerMs,
    };
    this.state = state;

    const keys =
      props.keys ?? (defaultKeys as NonNullable<(typeof props)['keys']>);
    const { dimensionItems, height, groupHeights, groupTops } =
      stackTimelineItems(
        props.items,
        props.groups,
        state.canvasTimeStart,
        state.canvasTimeEnd,
        pixelsPerMs,
        keys,
        props.lineHeight ?? defaultLineHeight,
        props.itemHeightRatio ?? defaultItemHeightRatio,
        props.stackItems ?? defaultStackItems,
        Boolean(state.draggingItem),
        Boolean(state.resizingItem),
        state.dragTime,
        state.resizingEdge,
        state.resizeTime,
        state.newGroupOrder,
      );

    state.dimensionItems = dimensionItems;
    state.height = height;
    state.groupHeights = groupHeights;
    state.groupTops = groupTops;
  }

  static getDerivedStateFromProps(
    nextProps: ReactCalendarTimelineProps,
    prevState: ReactCalendarTimelineState,
  ) {
    const { visibleTimeStart, visibleTimeEnd, items, groups } = nextProps;

    // This is a gross hack pushing items and groups in to state only to allow
    // For the forceUpdate check
    const derivedState: Partial<ReactCalendarTimelineState> = { items, groups };

    // if the items or groups have changed we must re-render
    const forceUpdate =
      items !== prevState.items || groups !== prevState.groups;

    // We are a controlled component
    if (visibleTimeStart && visibleTimeEnd) {
      // Get the new canvas position
      Object.assign(
        derivedState,
        calculateScrollCanvas(
          visibleTimeStart,
          visibleTimeEnd,
          forceUpdate,
          items,
          groups,
          nextProps,
          prevState,
        ),
      );
    } else if (forceUpdate) {
      // Calculate new item stack position as canvas may have changed
      const canvasWidth = getCanvasWidth(prevState.width);
      const { canvasTimeStart, canvasTimeEnd } = prevState;
      derivedState.timeRatio = coordinateToTimeRatio(
        canvasTimeStart,
        canvasTimeEnd,
        canvasWidth,
      );
      derivedState.pixelsPerMs = timeToCoordinateRatio(
        canvasTimeStart,
        canvasTimeEnd,
        canvasWidth,
      );
      const keys =
        nextProps.keys ??
        (defaultKeys as NonNullable<(typeof nextProps)['keys']>);
      Object.assign(
        derivedState,
        stackTimelineItems(
          items,
          groups,
          canvasTimeStart,
          canvasTimeEnd,
          derivedState.pixelsPerMs,
          keys,
          nextProps.lineHeight ?? defaultLineHeight,
          nextProps.itemHeightRatio ?? defaultItemHeightRatio,
          nextProps.stackItems ?? defaultStackItems,
          Boolean(prevState.draggingItem),
          Boolean(prevState.resizingItem),
          prevState.dragTime,
          prevState.resizingEdge,
          prevState.resizeTime,
          prevState.newGroupOrder,
        ),
      );
    }

    return derivedState;
  }

  getTimelineContext = (): TimelineContextValue => {
    const {
      width,
      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart,
      canvasTimeEnd,
    } = this.state;

    return {
      timelineWidth: width,
      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart,
      canvasTimeEnd,
    };
  };

  componentDidMount() {
    this.resize(this.props);

    if (this.props.resizeDetector && this.props.resizeDetector.addListener) {
      this.props.resizeDetector.addListener(this);
    }

    windowResizeDetector.addListener(this);

    this.lastTouchDistance = null;
  }

  componentWillUnmount() {
    if (this.props.resizeDetector && this.props.resizeDetector.removeListener) {
      this.props.resizeDetector.removeListener(this);
    }

    windowResizeDetector.removeListener(this);
  }

  componentDidUpdate(_prevProps: Props, prevState: ReactCalendarTimelineState) {
    const newZoom = this.state.visibleTimeEnd - this.state.visibleTimeStart;
    const oldZoom = prevState.visibleTimeEnd - prevState.visibleTimeStart;
    const newSidebarWidth = this.props.sidebarWidth ?? 200;

    // ensuring that sidebarWidth is properly updated
    // in the ReactCalendarTimeline component's state
    if (prevState.sidebarWidth !== newSidebarWidth) {
      this.setState({ sidebarWidth: newSidebarWidth });
    }

    // are we changing zoom? Report it!
    if (this.props.onZoom && newZoom !== oldZoom) {
      this.props.onZoom(this.getTimelineContext());
    }

    // The bounds have changed? Report it!
    if (
      this.props.onBoundsChange &&
      this.state.canvasTimeStart !== prevState.canvasTimeStart
    ) {
      this.props.onBoundsChange(
        this.state.canvasTimeStart,
        this.state.canvasTimeStart + newZoom * 3,
      );
    }

    // Check the scroll is correct
    const scrollLeft = Math.round(
      (this.state.width *
        (this.state.visibleTimeStart - this.state.canvasTimeStart)) /
        newZoom,
    );
    const componentScrollLeft = Math.round(
      (prevState.width *
        (prevState.visibleTimeStart - prevState.canvasTimeStart)) /
        oldZoom,
    );
    if (componentScrollLeft !== scrollLeft) {
      this.scrollComponent.scrollLeft = scrollLeft;
    }
  }

  resize = (props = this.props) => {
    const rect = this.container?.getBoundingClientRect();
    if (!rect) {
      setTimeout(() => this.resize(this.props), 0); // Add resize retry on next tick
      return;
    }
    const { width: containerWidth } = rect;

    const width = containerWidth;
    const canvasWidth = getCanvasWidth(width);
    const keys =
      props.keys ?? (defaultKeys as NonNullable<(typeof props)['keys']>);
    const { canvasTimeStart, canvasTimeEnd } = this.state;

    const timeRatio = coordinateToTimeRatio(
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
    );
    const pixelsPerMs = timeToCoordinateRatio(
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
    );

    const { dimensionItems, height, groupHeights, groupTops } =
      stackTimelineItems(
        props.items,
        props.groups,
        canvasTimeStart,
        canvasTimeEnd,
        pixelsPerMs,
        keys,
        props.lineHeight ?? defaultLineHeight,
        props.itemHeightRatio ?? defaultItemHeightRatio,
        props.stackItems ?? defaultStackItems,
        Boolean(this.state.draggingItem),
        Boolean(this.state.resizingItem),
        this.state.dragTime,
        this.state.resizingEdge,
        this.state.resizeTime,
        this.state.newGroupOrder,
      );

    // this is needed by dragItem since it uses pageY from the drag events
    // if this was in the context of the scrollElement, this would not be necessary

    this.setState({
      width,
      dimensionItems,
      height,
      groupHeights,
      groupTops,
      timeRatio,
      pixelsPerMs,
    });

    this.scrollComponent.scrollLeft = width;
  };

  onScroll = (scrollX: number) => {
    const width = this.state.width;

    const canvasTimeStart = this.state.canvasTimeStart;

    const zoom = this.state.visibleTimeEnd - this.state.visibleTimeStart;

    const visibleTimeStart = canvasTimeStart + (zoom * scrollX) / width;

    if (
      this.state.visibleTimeStart !== visibleTimeStart ||
      this.state.visibleTimeEnd !== visibleTimeStart + zoom
    ) {
      this.props.onTimeChange?.(
        visibleTimeStart,
        visibleTimeStart + zoom,
        this.updateScrollCanvas,
      );
    }
  };

  onScrollTime = (deltaT: number) => {
    if (!deltaT) return;
    const { visibleTimeStart, visibleTimeEnd } = this.state;
    const start = visibleTimeStart + deltaT;
    const end = visibleTimeEnd + deltaT;
    this.props.onTimeChange?.(start, end, this.updateScrollCanvas);
  };

  // called when the visible time changes
  updateScrollCanvas = (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    forceUpdateDimensions: boolean = false,
    items = this.props.items,
    groups = this.props.groups,
  ) => {
    this.setState(
      calculateScrollCanvas<CustomItem, CustomGroup, Props>(
        visibleTimeStart,
        visibleTimeEnd,
        forceUpdateDimensions,
        items,
        groups,
        this.props,
        this.state,
      ),
    );
  };

  handleWheelZoom = (speed: number, xPosition: number, deltaY: number) => {
    this.changeZoom(1.0 + (speed * deltaY) / 500, xPosition / this.state.width);
  };

  changeZoom = (scale: number, offset = 0.5) => {
    const { minZoom, maxZoom, minScale, maxScale, onTimeChange } = this.props;
    const { visibleTimeEnd, visibleTimeStart, width } = this.state;
    const oldZoom = visibleTimeEnd - visibleTimeStart;
    const newScale = Math.min(
      Math.max(minScale ?? defaultProps.minScale, scale),
      maxScale ?? defaultProps.maxScale,
    );
    const newZoom = Math.min(
      Math.max(Math.round(oldZoom * newScale), minZoom ?? defaultProps.minZoom),
      maxZoom ?? defaultProps.maxZoom,
    ); // min 1 min, max 20 years
    const sidebarWidth = this.state.sidebarWidth || this.props.sidebarWidth;
    if (sidebarWidth) {
      offset -= sidebarWidth / width;
    }
    const newVisibleTimeStart = Math.round(
      visibleTimeStart + (oldZoom - newZoom) * offset,
    );

    onTimeChange?.(
      newVisibleTimeStart,
      newVisibleTimeStart + newZoom,
      this.updateScrollCanvas,
    );
  };

  showPeriod = (from: number | Date | Moment, to: number | Date | Moment) => {
    const visibleTimeStart = from.valueOf();
    let visibleTimeEnd = to.valueOf();

    const width = this.state.width;
    const sidebarWidth =
      this.state.sidebarWidth ?? this.props.sidebarWidth ?? 200;
    const visibleFactor = (width - sidebarWidth) / width;

    const visibleTimeSpan = visibleTimeEnd - visibleTimeStart;
    const newVisibleTimeSpan = visibleTimeSpan / visibleFactor;
    const durationDueToSidebar = newVisibleTimeSpan - visibleTimeSpan;

    visibleTimeEnd += durationDueToSidebar;

    const zoom = visibleTimeEnd - visibleTimeStart;
    // can't zoom in more than to show one hour
    if (zoom < 360000) {
      return;
    }

    this.props.onTimeChange?.(
      visibleTimeStart,
      visibleTimeStart + zoom,
      this.updateScrollCanvas,
    );
  };

  showPeriodLegacy = (
    from: number | Date | Moment,
    to: number | Date | Moment,
  ) => {
    const visibleTimeStart = from.valueOf();
    const visibleTimeEnd = to.valueOf();

    const zoom = visibleTimeEnd - visibleTimeStart;
    // can't zoom in more than to show one hour
    if (zoom < 360000) {
      return;
    }

    this.props.onTimeChange?.(
      visibleTimeStart,
      visibleTimeStart + zoom,
      this.updateScrollCanvas,
    );
  };

  selectItem = (
    itemId: string | number | null,
    clickType?: 'click' | 'touch',
    e?: React.MouseEvent | (React.TouchEvent & Touch),
  ) => {
    if (
      this.isItemSelected(itemId) ||
      (this.props.itemTouchSendsClick && clickType === 'touch')
    ) {
      if (e && itemId && this.props.onItemClick) {
        const time = this.timeFromItemEvent(e);
        this.props.onItemClick(itemId, e, time);
      }
    } else {
      this.setState({ selectedItem: itemId });
      if (itemId && e && this.props.onItemSelect) {
        const time = this.timeFromItemEvent(e);
        this.props.onItemSelect(itemId, e, time);
      } else if (itemId === null && this.props.onItemDeselect) {
        this.props.onItemDeselect(e); // this isn't in the docs. Is this function even used?
      }
    }
  };

  doubleClickItem = (item: string | number, e: React.MouseEvent) => {
    if (this.props.onItemDoubleClick) {
      const time = this.timeFromItemEvent(e);
      this.props.onItemDoubleClick(item, e, time);
    }
  };

  tapItem = (item: string | number, e: React.TouchEvent<HTMLElement>) => {
    if (this.props.onItemTap) {
      this.props.onItemTap(item, e);
    }
  };

  contextMenuClickItem = (item: string | number, e: React.MouseEvent) => {
    if (this.props.onItemContextMenu) {
      const time = this.timeFromItemEvent(e);
      this.props.onItemContextMenu(item, e, time);
    }
  };

  // TODO: this is very similar to timeFromItemEvent, aside from which element to get offsets
  // from.  Look to consolidate the logic for determining coordinate to time
  // as well as generalizing how we get time from click on the canvas
  getTimeFromRowClickEvent = (e: React.MouseEvent) => {
    const { dragSnap } = this.props;
    const { canvasTimeStart, timeRatio } = this.state;
    // this gives us distance from left of row element, so event is in
    // context of the row element, not client or page
    const { offsetX } = e.nativeEvent;

    let time = calculateTimeForXPosition(canvasTimeStart, timeRatio, offsetX);
    const snap = dragSnap ?? defaultProps.dragSnap;
    time = Math.floor(time / snap) * snap;

    return time;
  };

  timeFromItemEvent = (e: React.MouseEvent | (React.TouchEvent & Touch)) => {
    const { width, visibleTimeStart, visibleTimeEnd } = this.state;
    const { dragSnap } = this.props;

    const scrollComponent = this.scrollComponent;
    const { left: scrollX } = scrollComponent.getBoundingClientRect();

    const xRelativeToTimeline = e.clientX - scrollX;

    const relativeItemPosition = xRelativeToTimeline / width;
    const zoom = visibleTimeEnd - visibleTimeStart;
    const timeOffset = relativeItemPosition * zoom;

    let time = Math.round(visibleTimeStart + timeOffset);
    const snap = dragSnap ?? defaultProps.dragSnap;
    time = Math.floor(time / snap) * snap;

    return time;
  };

  dragItem = (
    item: string | number,
    dragTime: number,
    newGroupOrder: number,
  ) => {
    const newGroup = this.props.groups[newGroupOrder];
    const keys = this.props.keys ?? defaultKeys;

    this.setState({
      draggingItem: item,
      dragTime: dragTime,
      newGroupOrder: newGroupOrder,
      dragGroupTitle: newGroup
        ? _get(newGroup, keys.groupLabelKey as keyof CustomGroup)
        : '',
    });

    this.updatingItem({
      eventType: 'move',
      itemId: item,
      time: dragTime,
      newGroupOrder,
    });
  };

  dropItem = (
    item: string | number,
    dragTime: number,
    newGroupOrder: number,
  ) => {
    this.setState({ draggingItem: null, dragTime: null, dragGroupTitle: null });
    if (this.props.onItemMove) {
      this.props.onItemMove(item, dragTime, newGroupOrder);
    }
  };

  resizingItem = (
    item: string | number,
    resizeTime: number,
    edge: null | 'left' | 'right',
  ) => {
    this.setState({
      resizingItem: item,
      resizingEdge: edge,
      resizeTime: resizeTime,
    });

    this.updatingItem({
      eventType: 'resize',
      itemId: item,
      time: resizeTime,
      edge,
    });
  };

  resizedItem = (
    item: string | number,
    resizeTime: number,
    edge: null | 'left' | 'right',
    timeDelta: number,
  ) => {
    this.setState({ resizingItem: null, resizingEdge: null, resizeTime: null });
    if (this.props.onItemResize && timeDelta !== 0) {
      this.props.onItemResize(item, resizeTime, edge);
    }
  };

  updatingItem = (data: ItemDragData) => {
    if (this.props.onItemDrag) {
      this.props.onItemDrag(data);
    }
  };

  columns(
    canvasTimeStart: number,
    canvasTimeEnd: number,
    minUnit: Unit,
    timeSteps: TimeSteps,
    height: number,
  ) {
    return (
      <Columns
        canvasTimeStart={canvasTimeStart}
        canvasTimeEnd={canvasTimeEnd}
        minUnit={minUnit}
        timeSteps={timeSteps}
        height={height}
        verticalLineClassNamesForTime={this.props.verticalLineClassNamesForTime}
        isReadOnlyScenario={this.props.isReadOnlyScenario}
      />
    );
  }

  handleRowClick = (e: React.MouseEvent, rowIndex: number) => {
    // shouldn't this be handled by the user, as far as when to deselect an item?
    if (this.hasSelectedItem()) {
      this.selectItem(null);
    }

    if (this.props.onCanvasClick == null) return;

    const time = this.getTimeFromRowClickEvent(e);
    const groupId = _get(
      this.props.groups[rowIndex],
      (this.props.keys ?? defaultKeys).groupIdKey as keyof CustomGroup,
    ) as string;
    this.props.onCanvasClick(groupId, time, e);
  };

  handleRowDoubleClick = (e: React.MouseEvent, rowIndex: number) => {
    if (this.props.onCanvasDoubleClick == null) return;

    const time = this.getTimeFromRowClickEvent(e);
    const groupId = _get(
      this.props.groups[rowIndex],
      (this.props.keys ?? defaultKeys).groupIdKey as keyof CustomGroup,
    ) as string;
    this.props.onCanvasDoubleClick(groupId, time, e);
  };

  handleScrollContextMenu = (e: React.MouseEvent, rowIndex: number) => {
    if (this.props.onCanvasContextMenu == null) return;

    const time = this.getTimeFromRowClickEvent(e);

    const groupId = _get(
      this.props.groups[rowIndex],
      (this.props.keys ?? defaultKeys).groupIdKey as keyof CustomGroup,
    ) as string;

    if (this.props.onCanvasContextMenu) {
      e.preventDefault();
      this.props.onCanvasContextMenu(groupId, time, e);
    }
  };

  rows(canvasWidth: number, groupHeights: number[], groups: CustomGroup[]) {
    return (
      <GroupRows
        groups={groups}
        canvasWidth={canvasWidth}
        lineCount={_length(this.props.groups)}
        groupHeights={groupHeights}
        clickTolerance={
          this.props.clickTolerance ?? defaultProps.clickTolerance
        }
        onRowClick={this.handleRowClick}
        onRowDoubleClick={this.handleRowDoubleClick}
        horizontalLineClassNamesForGroup={
          this.props.horizontalLineClassNamesForGroup
        }
        onRowContextClick={this.handleScrollContextMenu}
        isReadOnlyScenario={this.props.isReadOnlyScenario}
      />
    );
  }

  items(
    canvasTimeStart: number,
    canvasTimeEnd: number,
    canvasWidth: number,
    dimensionItems: ItemDimensions[],
    groupTops: number[],
    headerHeight: number,
    timeRatio: number,
    pixelsPerMs: number,
  ) {
    const keys =
      this.props.keys ??
      (defaultKeys as NonNullable<(typeof this.props)['keys']>);
    return (
      <Items
        canvasTimeStart={canvasTimeStart}
        canvasTimeEnd={canvasTimeEnd}
        canvasWidth={canvasWidth}
        canvasTop={headerHeight}
        dimensionItems={dimensionItems}
        groupTops={groupTops}
        items={this.props.items}
        groups={this.props.groups}
        keys={keys}
        selectedItem={this.state.selectedItem}
        dragSnap={this.props.dragSnap}
        minResizeWidth={this.props.minResizeWidth}
        canChangeGroup={this.props.canChangeGroup}
        canMove={this.props.canMove}
        canResize={this.props.canResize}
        useResizeHandle={this.props.useResizeHandle}
        canSelect={this.props.canSelect}
        moveResizeValidator={this.props.moveResizeValidator}
        itemSelect={this.selectItem}
        itemDrag={this.dragItem}
        itemDrop={this.dropItem}
        onItemDoubleClick={this.doubleClickItem}
        onItemTap={this.tapItem}
        onItemContextMenu={this.contextMenuClickItem}
        itemResizing={this.resizingItem}
        itemResized={this.resizedItem}
        itemRenderer={this.props.itemRenderer}
        selected={this.props.selected}
        scrollRef={this.scrollComponent}
        getTimelineContext={this.getTimelineContext}
        timeRatio={timeRatio}
        pixelsPerMs={pixelsPerMs}
      />
    );
  }

  handleHeaderRef = (el: HTMLDivElement | null) => {
    this.props.headerRef?.(el);
  };

  onResizeSidebar = (isRightSidebar: boolean, width: number) => {
    if (isRightSidebar) {
      this.setState({ rightSidebarWidth: width });
    } else {
      this.setState({ sidebarWidth: width });
    }
  };

  sidebar(height: number, groupHeights: number[], headerHeight: number) {
    const sidebarWidth = this.state.sidebarWidth || this.props.sidebarWidth;
    const keys =
      this.props.keys ??
      (defaultKeys as NonNullable<(typeof this.props)['keys']>);
    return (
      sidebarWidth && (
        <Sidebar
          groups={this.props.groups}
          groupRenderer={this.props.groupRenderer}
          keys={keys}
          onResizeSidebar={this.onResizeSidebar}
          width={sidebarWidth}
          headerHeight={headerHeight}
          groupHeights={groupHeights}
          height={height}
        />
      )
    );
  }

  rightSidebar(height: number, groupHeights: number[], headerHeight: number) {
    const rightSidebarWidth =
      this.state.rightSidebarWidth || this.props.rightSidebarWidth;
    const keys =
      this.props.keys ??
      (defaultKeys as NonNullable<(typeof this.props)['keys']>);
    return (
      rightSidebarWidth && (
        <Sidebar
          groups={this.props.groups}
          keys={keys}
          groupRenderer={this.props.groupRenderer}
          onResizeSidebar={this.onResizeSidebar}
          isRightSidebar
          width={rightSidebarWidth}
          headerHeight={headerHeight}
          groupHeights={groupHeights}
          height={height}
        />
      )
    );
  }

  /**
   * check if child of type TimelineHeader
   * refer to for explanation https://github.com/gaearon/react-hot-loader#checking-element-types
   */
  isTimelineHeader = (child: React.ReactNode) => {
    if (!child || typeof child !== 'object' || !('type' in child)) {
      return false;
    }
    const type = child.type;
    if (
      type === undefined ||
      typeof type === 'string' ||
      !('secretKey' in type)
    ) {
      return false;
    }
    return type.secretKey === TimelineHeaders.secretKey;
  };

  childrenWithProps(
    canvasTimeStart: number,
    canvasTimeEnd: number,
    canvasWidth: number,
    dimensionItems: ItemDimensions[],
    groupHeights: number[],
    groupTops: number[],
    height: number,
    visibleTimeStart: number,
    visibleTimeEnd: number,
    minUnit: Unit,
    timeSteps: TimeSteps,
  ) {
    const children = this.props.children;
    if (!children) {
      return null;
    }

    const childProps = {
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
      visibleTimeStart: visibleTimeStart,
      visibleTimeEnd: visibleTimeEnd,
      dimensionItems,
      items: this.props.items,
      groups: this.props.groups,
      keys: this.props.keys,
      groupHeights: groupHeights,
      groupTops: groupTops,
      selected: this.getSelected(),
      height: height,
      minUnit: minUnit,
      timeSteps: timeSteps,
    };

    return React.Children.map(children, (child) => {
      if (!this.isTimelineHeader(child)) {
        return React.isValidElement(child)
          ? React.cloneElement(child, childProps)
          : child;
      }
    });
  }

  renderHeaders = () => {
    const children = this.props.children;
    if (Array.isArray(children)) {
      for (const child of children) {
        if (this.isTimelineHeader(child)) {
          return child;
        }
      }
    }
    return (
      <TimelineHeaders>
        <DateHeader unit="primaryHeader" />
        <DateHeader />
      </TimelineHeaders>
    );
  };

  getSelected() {
    const selectedItem = this.state.selectedItem;
    const selected = this.props.selected;
    return selectedItem && !selected ? [selectedItem] : selected || [];
  }

  hasSelectedItem() {
    const selected = this.props.selected;
    return Array.isArray(selected)
      ? selected.length > 0
      : this.state.selectedItem !== null;
  }

  isItemSelected(itemId: string | number | null) {
    return itemId !== null && this.getSelected().includes(itemId);
  }

  getScrollElementRef = (el: HTMLDivElement) => {
    this.props.scrollRef?.(el);
    this.scrollComponent = el;
  };

  refHandler = (el: HTMLDivElement) => {
    this.container = el;
    if (el) this.resize(this.props);
  };

  render() {
    const { items, groups, timeSteps, headerHeight = 60 } = this.props;
    const {
      draggingItem,
      resizingItem,
      width,
      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart,
      canvasTimeEnd,
      timeRatio,
      pixelsPerMs,
    } = this.state;
    const sidebarWidth =
      (this.state.sidebarWidth || this.props.sidebarWidth) ??
      defaultProps.sidebarWidth;
    const rightSidebarWidth =
      (this.state.rightSidebarWidth || this.props.rightSidebarWidth) ??
      defaultProps.rightSidebarWidth;
    let { dimensionItems, height, groupHeights, groupTops } = this.state;

    const zoom = visibleTimeEnd - visibleTimeStart;
    const canvasWidth = getCanvasWidth(width);
    const minUnit = getMinUnit(
      zoom,
      width,
      timeSteps ?? defaultProps.timeSteps,
    );

    const isInteractingWithItem = !!draggingItem || !!resizingItem;

    if (isInteractingWithItem) {
      const keys =
        this.props.keys ??
        (defaultKeys as NonNullable<(typeof this.props)['keys']>);
      const stackResults = stackTimelineItems(
        items,
        groups,
        this.state.canvasTimeStart,
        this.state.canvasTimeEnd,
        this.state.pixelsPerMs,
        keys,
        this.props.lineHeight ?? defaultLineHeight,
        this.props.itemHeightRatio ?? defaultItemHeightRatio,
        this.props.stackItems ?? defaultStackItems,
        Boolean(this.state.draggingItem),
        Boolean(this.state.resizingItem),
        this.state.dragTime,
        this.state.resizingEdge,
        this.state.resizeTime,
        this.state.newGroupOrder,
      );
      dimensionItems = stackResults.dimensionItems;
      height = stackResults.height;
      groupHeights = stackResults.groupHeights;
      groupTops = stackResults.groupTops;
    }

    return (
      <TimelineStateProvider
        visibleTimeStart={visibleTimeStart}
        visibleTimeEnd={visibleTimeEnd}
        canvasTimeStart={canvasTimeStart}
        canvasTimeEnd={canvasTimeEnd}
        canvasWidth={canvasWidth}
        canvasHeight={height}
        timeRatio={timeRatio}
        pixelsPerMs={pixelsPerMs}
        showPeriod={this.showPeriod}
        showPeriodLegacy={this.showPeriodLegacy}
        timelineUnit={minUnit}
        timelineWidth={this.state.width}
      >
        <TimelineMarkersProvider>
          <TimelineHeadersProvider
            registerScroll={this.handleHeaderRef}
            timeSteps={timeSteps ?? defaultProps.timeSteps}
            leftSidebarWidth={sidebarWidth}
            rightSidebarWidth={rightSidebarWidth}
          >
            <div
              style={this.props.style}
              ref={this.refHandler}
              className="react-calendar-timeline"
            >
              <div className="rct-outer">
                <ScrollElement
                  scrollRef={this.getScrollElementRef}
                  width={width}
                  onZoom={this.changeZoom}
                  onWheelZoom={this.handleWheelZoom}
                  onScroll={this.onScroll}
                  onScrollTime={this.onScrollTime}
                  onResizeSidebar={this.onResizeSidebar}
                  isInteractingWithItem={isInteractingWithItem}
                  timeFromItemEvent={this.timeFromItemEvent}
                >
                  {this.renderHeaders()}
                  {sidebarWidth > 0
                    ? this.sidebar(height, groupHeights, headerHeight)
                    : null}
                  <div style={{ position: 'relative', display: 'inline' }}>
                    <MarkerCanvas>
                      {this.columns(
                        canvasTimeStart,
                        canvasTimeEnd,
                        minUnit,
                        timeSteps ?? defaultProps.timeSteps,
                        height,
                      )}
                      {this.rows(canvasWidth, groupHeights, groups)}
                      {this.items(
                        canvasTimeStart,
                        canvasTimeEnd,
                        canvasWidth,
                        dimensionItems,
                        groupTops,
                        headerHeight,
                        timeRatio,
                        pixelsPerMs,
                      )}
                      {this.childrenWithProps(
                        canvasTimeStart,
                        canvasTimeEnd,
                        canvasWidth,
                        dimensionItems,
                        groupHeights,
                        groupTops,
                        height,
                        visibleTimeStart,
                        visibleTimeEnd,
                        minUnit,
                        timeSteps ?? defaultProps.timeSteps,
                      )}
                    </MarkerCanvas>
                  </div>
                </ScrollElement>
                {rightSidebarWidth > 0
                  ? this.rightSidebar(height, groupHeights, headerHeight)
                  : null}
              </div>
            </div>
          </TimelineHeadersProvider>
        </TimelineMarkersProvider>
      </TimelineStateProvider>
    );
  }
}
